import type { AppointmentItem } from '@heidi/integrations/ehr/contracts';
import type { components } from '@heidi/schema/ml-scribe';
import type { SessionByDateItem } from '@heidi/session/contracts';
import type { ClinicalTaskResultTask } from '@heidi/tasks/contracts';
import type {
  Annotation,
  CalculatorResult,
  EvidenceImage,
  FormulaCalculationStep,
  FormulaCalculatorCategory,
  FormulaCalculatorResult,
  FormulaCriteriaBreakdown,
  InteractiveWidgetKind,
  SimpleCitation,
  Source,
  VisualDxDetailResult,
  VisualDxDiagnosis,
  VisualDxRankedDifferentialEntry,
} from '../answers';
import type { FollowUpQuestion } from '../followUpQuestions';
import type {
  VisualDxBuiltDifferentialPayload,
  VisualDxComparePayload,
  VisualDxDrugReactionPayload,
  VisualDxFindingsPayload,
  VisualDxGalleryPayload,
  VisualDxHandoutPayload,
} from '../interactive-widgets';

// ===== CAPACITY STATUS =====
export type CapacityStatus = 'ok' | 'warning' | 'critical' | 'exceeded';

export interface CapacityChunk {
  type: 'capacity';
  status: CapacityStatus;
  document_size_bytes: number;
}

// ===== CHAT MODE =====
export enum ChatMode {
  // EVIDENCE mode supports versioning:
  // - version 1: GPT-5 + LangChain web search
  // - version 2: Anthropic claude-sonnet + native web search
  EVIDENCE = 'evidence',
  LEGACY = 'legacy', // Ask Heidi: Gemini 2.5 Pro with legacy ask-ai prompts
  AUTO = 'auto', // Auto: Intelligent routing using gpt-4o-mini to classify query
}

// ===== EVIDENCE SOURCE FILTER (EVI-493) =====
//
// `EvidenceSourceFilterValue` and `EvidenceSourceFilters` moved to
// `@heidi/evidence/contracts`. They are read by the atom layer, this transport
// and the timeline event source, and no re-export is left here: a re-export
// would keep every reader resolving through Chat, which is the coupling the
// move removes.
// ===== EVIDENCE ANSWER CONTRACTS =====
//
// The answer types this module used to declare — the source and citation
// shapes, the calculator, VisualDx and interactive-widget results, and the
// clinical-task / session / template / appointment / patient result items —
// live in `@heidi/evidence/contracts` and are imported above. The split is
// envelope versus payload: the chunk and timeline-entry types below are Chat's
// stream mechanics and stay, and no re-export is left here, so every reader of
// an answer type resolves through Evidence's door rather than through Chat.
// The two citation predicates stayed with Chat because they are runtime values.
// ===== CHUNK TYPES (Preserved from Backend) =====
// Represents a single summary text block within a Reasoning object
export interface SummaryItem {
  index: number;
  type: 'summary_text';
  text: string;
}

// Model for the 'reasoning' type object
export interface Reasoning {
  id: string;
  summary: SummaryItem[];
  type: 'reasoning';
  index: number;
}

// Represents the search action within a WebSearchCall
export interface Action {
  query: string;
  type: 'search';
  sources: Source[];
  images?: EvidenceImage[];
}

// Model for the 'web_search_call' type object
export interface WebSearchCall {
  id: string;
  action: Action;
  status: 'searching' | 'completed';
  type: 'web_search_call';
  index: number;
}

// Model for the 'library_search_call' type object (user's uploaded documents)
export interface LibrarySearchCall {
  id: string;
  action: Action;
  status: 'searching' | 'completed';
  type: 'library_search_call';
  index: number;
}

// Model for the 'patient_summary' type object (EVI-591: Pre-chart tool events)
export interface PatientSummaryChunk {
  id: string;
  type: 'patient_summary';
  status: 'fetching' | 'completed' | 'error';
  patient_profile_id: string;
  error?: string; // Error message when status is 'error'
  index: number;
}

// Model for the 'capability_tool' type object (Protocol capability tool events)
export interface CapabilityToolChunk {
  id: string;
  type: 'capability_tool';
  name: string; // Tool name (e.g., 'get_patient_summary', 'execute_template_workflow')
  status: 'executing' | 'completed' | 'error';
  error?: string; // Error message when status is 'error'
  index: number;
}

// Model for the 'text' type object
export interface TextResult {
  type: 'text';
  text: string;
  index: number;
  annotations?: Annotation[];
  citations?: SimpleCitation[]; // EVIDENCE mode: inline citations per chunk
  groupId?: string; // EVIDENCE mode: groups text blocks for inline rendering
  id?: string;
  quota_exceeded?: string; // Set when this text chunk is a quota paywall message
  // EVI-1567 / EV-023: set when this answer references a drug, so a
  // drug-interaction warning badge is rendered above the response.
  drugReferenced?: boolean;
}

// Model for the 'drug_referenced' type object (EVI-1567 / EV-023).
// A one-time, index-less signal emitted by the backend the first time an
// Evidence answer references a drug (gated behind the
// `release-evidence-enable-drug-disclaimer` flag). The client uses it to mark
// the streaming response so a drug-interaction warning badge is shown above it.
export interface DrugReferencedChunk {
  type: 'drug_referenced';
  groupId?: string;
}

// Model for the 'chat_metadata' type object (used for non-blocking title updates)
export interface ChatMetadataChunk {
  type: 'chat_metadata';
  title: string;
  chat_id: string;
}

export type ChatMetadataChunkUnion = ChatMetadataChunk;

// Model for the 'mode_info' type object (communicates resolved mode to frontend)
// Sent early in stream when user selects 'auto' mode, telling frontend which mode was classified
export interface ModeInfoChunk {
  type: 'mode_info';
  mode: 'legacy' | 'evidence'; // Resolved mode (never 'auto')
}

// Ambient quota update emitted at the end of an in-session evidence turn.
// Carries the post-consume in-session evidence usage so the chat panel's
// quota badge / banner can re-render without a /user refetch round-trip.
// `consumed: false` means the user was already at the cap (frontend is
// the soft enforcement boundary; backend just meters).
export interface InSessionEvidenceQuotaUpdateChunk {
  type: 'in_session_evidence_quota_update';
  kind: 'basic' | 'advanced';
  consumed: boolean;
  evidence_usage: components['schemas']['EvidenceUsageResponse'];
}

/**
 * Chunk sent when the user has exhausted their in-session evidence quota.
 * Field names match backend (snake_case).
 */
export interface QuotaExhaustedChunk {
  type: 'quota_exhausted';
  index: number;
  message: string;
  upgrade_url: string;
}

/**
 * Timeline entry for quota-exhausted paywall turns.
 */
export interface QuotaExhaustedItemEntry {
  type: 'quota_exhausted_item';
  id: string;
  message: string;
  upgradeUrl: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Model for the 'search_metadata' type object (used for analytics tracking)
export interface SearchMetadataChunk {
  type: 'search_metadata';
  tavily_search_count: number;
  index: -1;
}

// Model for the 'thinking' type object (EVI-334: Extended thinking mode)
export interface ThinkingChunk {
  type: 'thinking';
  status: 'thinking' | 'streaming' | 'complete';
  thinking?: string; // Thinking content (streamed or complete)
  duration_ms?: number; // Duration in ms (only when status: 'complete')
  index: number;
  groupId?: string;
}

// Model for the 'pdf_export' type object
//
// NOTE on `download_url` (applies to all *Chunk types in this file):
// The backend now returns a durable URL pointing at
// `/api/v2/ml-scribe/chat/exports/download`, which 302-redirects to a
// freshly-minted S3 presigned URL on every click. Treat the value as an
// opaque, long-lived URL — open it with `window.open` / `<a href>` and the
// browser will transparently follow the redirect to S3. No expiry handling
// on the client.
export interface PdfExportChunk {
  type: 'pdf_export';
  id: string;
  status: 'generating' | 'completed';
  title: string;
  index: number;
  download_url?: string; // Only present when status: 'completed'
  filename?: string; // Only present when status: 'completed'
}

// Model for the 'print_document' type object (web-only)
// On 'completed' the client auto-opens the browser print dialog.
export interface PrintDocumentChunk {
  type: 'print_document';
  id: string;
  status: 'generating' | 'completed' | 'error';
  title: string;
  index: number;
  action?: 'print'; // Signals the client to open the print dialog
  download_url?: string; // Only present when status: 'completed'
  filename?: string; // Only present when status: 'completed'
  error?: string; // Only present when status: 'error'
}

// Model for the 'patient_evidence_pdf' type object
export interface PatientEvidencePdfChunk {
  type: 'patient_evidence_pdf';
  id: string;
  status: 'generating' | 'completed' | 'error';
  title: string;
  index: number;
  download_url?: string; // Only present when status: 'completed'
  filename?: string; // Only present when status: 'completed'
  conditions_included?: number; // Number of conditions in the PDF
  error?: string; // Error message when status: 'error'
}

// Model for the 'email_patient_evidence' type object
export interface EmailPatientEvidenceChunk {
  type: 'email_patient_evidence';
  id: string;
  status: 'sending' | 'completed' | 'error';
  patient_email: string;
  index: number;
  message?: string; // Success message when status: 'completed'
  error?: string; // Error message when status: 'error'
  needs_email?: boolean; // True if email address is invalid/missing
  needs_pdf?: boolean; // True if PDF needs to be generated first
}

// Model for the 'form_fill' type object (PDF form filling via session context)
export interface FormFillChunk {
  type: 'form_fill';
  id: string;
  status: 'filling' | 'completed' | 'error' | 'needs_template';
  title: string;
  index: number;
  download_url?: string;
  filename?: string;
  fields_filled?: number;
  error?: string;
}

// Model for the 'medical_calculator' type object (EVI-544)
export interface MedicalCalculatorChunk {
  type: 'medical_calculator';
  id: string;
  calculator: string; // Calculator ID (e.g., "wells_dvt", "egfr_ckd_epi_2021")
  status: 'calculating' | 'completed' | 'error';
  index: number;
  // Result (only present when status: 'completed')
  result?: CalculatorResult;
  // Error message (only present when status: 'error')
  error?: string;
}

export interface VisualDxChunk {
  type: 'visualdx';
  id: string;
  tool: 'get_diagnostic_info' | 'enrich_diagnoses_with_links';
  status: 'fetching' | 'completed' | 'error';
  query?: string;
  index: number;
  error?: string;
  /** Present on completed ``enrich_diagnoses_with_links`` chunks. */
  diagnoses?: VisualDxDiagnosis[];
  /** Compare-all URL present on completed ``enrich_diagnoses_with_links`` chunks. */
  compare_url?: string | null;
  /** Present on completed ``get_diagnostic_info`` chunks. */
  detail?: VisualDxDetailResult;
}

/**
 * Generic chunk that mounts an inline interactive widget in the chat as
 * a Heidi-native React component. The widget runs client-side and hands
 * its typed selections back to the chat via the
 * ``InteractiveWidgetBlock`` ``onSubmit`` prop.
 *
 * ``kind`` distinguishes which widget surface this chunk represents — the
 * FE looks up the renderer + result formatter from the ``kind`` so a
 * single chunk type / block serves all widgets.
 */
export interface InteractiveWidgetChunk {
  type: 'interactive_widget';
  id: string;
  kind: InteractiveWidgetKind;
  status: 'fetching' | 'completed' | 'error';
  query?: string;
  index: number;
  error?: string;
  /**
   * Present on completed ``visualdx_ranked_differential`` chunks. The BE
   * merges the LLM's proposed ranking with VisualDx MCP enrichment
   * (image + link) and streams the full payload in a single chunk.
   */
  differential?: VisualDxRankedDifferentialEntry[];
  /** Present on completed ``visualdx_gallery`` chunks (Module 3). */
  gallery?: VisualDxGalleryPayload;
  /** Present on completed ``visualdx_compare`` chunks (Module 4). */
  compare?: VisualDxComparePayload;
  /** Present on completed ``visualdx_drug_ddx`` chunks (Module 2). */
  drugReaction?: VisualDxDrugReactionPayload;
  findings?: VisualDxFindingsPayload;
  builtDifferential?: VisualDxBuiltDifferentialPayload;
  handout?: VisualDxHandoutPayload;
}

// Model for the 'calculator_result' type object (EVI-620: Dynamic Formula Calculator)
// This is the new dynamic calculator that uses evidence_search for formula discovery
export interface FormulaCalculatorChunk {
  type: 'calculator_result';
  id: string;
  status: 'calculating' | 'completed' | 'error';
  formula_name: string;
  formula_expression?: string; // Actual mathematical expression (e.g., "a + b - 2*c")
  formula_display?: string; // Human-readable formula
  category: FormulaCalculatorCategory;
  variables?: Record<string, number | string | boolean>;
  result?: FormulaCalculatorResult;
  source_url?: string;
  source_name?: string;
  validation_reference?: string;
  criteria_breakdown?: FormulaCriteriaBreakdown[];
  calculation_steps?: FormulaCalculationStep[];
  warnings?: string[];
  error?: string;
  index: number;
}

// Model for the 'clinical_task_result' type object (EVI-490)
export interface ClinicalTaskResultChunk {
  type: 'clinical_task_result';
  id: string;
  status: 'creating' | 'completed' | 'failed';
  tasks: ClinicalTaskResultTask[];
  index: number;
  error?: string; // Error message when status: 'failed'
}

// Model for the 'sessions_by_date' type object (Patients in a Day feature)
export interface SessionsByDateChunk {
  type: 'sessions_by_date';
  id: string;
  status: 'fetching' | 'completed' | 'error';
  date: string;
  total_sessions?: number;
  has_more?: boolean; // True if more sessions exist beyond the returned set (max 50)
  sessions?: SessionByDateItem[];
  index: number;
  error?: string; // Error message when status: 'error'
}

// ===== NEW TOOL CHUNK TYPES =====
// Model for the 'appointments' type object
export interface AppointmentsChunk {
  type: 'appointments';
  id: string;
  status: 'fetching' | 'completed' | 'error';
  date?: string;
  total_appointments?: number;
  appointments?: AppointmentItem[];
  index: number;
  error?: string;
}

// Model for the 'search_templates' type object
export interface SearchTemplatesChunk {
  type: 'search_templates';
  id: string;
  status: 'searching' | 'completed' | 'error';
  query?: string;
  total_results?: number;
  index: number;
  error?: string;
}

// Model for the 'search_community_templates' type object
export interface SearchCommunityTemplatesChunk {
  type: 'search_community_templates';
  id: string;
  status: 'searching' | 'completed' | 'error';
  query?: string;
  total_results?: number;
  index: number;
  error?: string;
}

// Model for the 'search_my_templates' type object
export interface SearchMyTemplatesChunk {
  type: 'search_my_templates';
  id: string;
  status: 'searching' | 'completed' | 'error';
  query?: string;
  total_results?: number;
  index: number;
  error?: string;
}

// Model for the 'create_template' type object. Ask Heidi creates a new personal template.
export interface CreateTemplateChunk {
  type: 'create_template';
  id: string;
  status: 'applying' | 'completed' | 'error';
  index: number;
  template_id?: string;
  template_name?: string;
  reason?: string;
  error?: string;
}

// Model for the 'edit_template_section' type object. Ask Heidi edits an existing template.
export interface EditTemplateSectionChunk {
  type: 'edit_template_section';
  id: string;
  status: 'applying' | 'completed' | 'error';
  index: number;
  template_id?: string;
  template_name?: string;
  edits_count?: number;
  edits_applied?: number;
  reason?: string;
  error?: string;
}

// Model for the 'get_template_and_preferences' type object
export interface GetTemplatePrefsChunk {
  type: 'get_template_and_preferences';
  id: string;
  status: 'fetching' | 'completed' | 'error';
  index: number;
  error?: string;
  template_available?: boolean;
  template_name?: string | null;
}

// Model for the 'search_patient_profiles' type object
export interface SearchPatientProfilesChunk {
  type: 'search_patient_profiles';
  id: string;
  status: 'searching' | 'completed' | 'error';
  query?: string;
  total_results?: number;
  index: number;
  error?: string;
}

// Model for the 'csv_export' type object (CSV Export feature)
export interface CsvExportChunk {
  type: 'csv_export';
  id: string;
  status: 'generating' | 'completed' | 'error';
  filename: string;
  index: number;
  download_url?: string; // Only present when status: 'completed'
  row_count?: number; // Number of rows in the CSV
  error?: string; // Error message when status: 'error'
}

// Model for the 'word_export' type object (Word Document Export feature)
export interface WordExportChunk {
  type: 'word_export';
  id: string;
  status: 'generating' | 'completed' | 'error';
  filename: string;
  index: number;
  download_url?: string; // Only present when status: 'completed'
  session_count?: number; // Number of sessions in the document
  error?: string; // Error message when status: 'error'
}

// Model for the 'excel_export' type object (Excel Spreadsheet Export feature)
export interface ExcelExportChunk {
  type: 'excel_export';
  id: string;
  status: 'generating' | 'completed' | 'error';
  filename: string;
  index: number;
  download_url?: string; // Only present when status: 'completed'
  row_count?: number; // Number of rows in the spreadsheet
  error?: string; // Error message when status: 'error'
}

// Model for the 'pptx_export' type object (PowerPoint Presentation Export feature)
export interface PptxExportChunk {
  type: 'pptx_export';
  id: string;
  status: 'generating' | 'completed' | 'error';
  filename: string;
  index: number;
  download_url?: string;
  preview_url?: string;
  slide_count?: number;
  presentation_id?: string;
  error?: string;
}

// Model for the 'message_info' type object (backend message ID tracking)
export interface MessageInfoChunk {
  type: 'message_info';
  message_id: string;
  index: number;
  /**
   * EVI-2117: when `true`, the backend guarantees it emits a terminal
   * `stream_complete` sentinel as the last chunk of a fully-successful stream.
   * The client uses this to treat a clean SSE close *without* that sentinel as a
   * truncated (non-success) turn. Absent on pre-sentinel backends, where the
   * client cannot distinguish truncation from success and must assume success.
   */
  supports_terminal_sentinel?: boolean;
}

/**
 * Chunk sent when an unauthenticated user triggers a tool that requires auth.
 * Rendered as a dedicated block with error message and sign-up CTA.
 * Field names match backend (snake_case).
 */
export interface UnauthenticatedToolAccessErrorChunk {
  type: 'unauthenticated_tool_access_error';
  id: string;
  tool_name: string;
  status: 'unauthorized';
  message: string;
  index: number;
}

/**
 * Represents an unknown/unsupported tool call chunk from the backend.
 * This is a catch-all for tool types that the frontend doesn't yet have
 * a dedicated component for — typically when the web release is ahead
 * of the desktop release. The frontend will render a generic fallback UI.
 */
export interface UnsupportedToolChunk {
  type: string; // The unknown tool type from the backend
  id?: string;
  status?: string;
  index: number;
  [key: string]: unknown; // Preserve any extra payload fields
}

/**
 * In-band SSE refusal. HTTP 200 with this chunk is a failed turn, not a tool.
 * `message` may echo account state and must not be rendered or logged.
 */
export interface StreamErrorChunk {
  type: 'error';
  error_type: string;
  message?: string;
  retry_after_seconds?: number;
}

// Union type for all possible timeline chunks (preserved)
// NOTE: UnsupportedToolChunk is intentionally excluded from this union.
// It is used as a cast target in processChatChunk when an unknown chunk type
// is encountered, allowing the discriminated union to remain strict.
export type Chunk =
  | Reasoning
  | WebSearchCall
  | LibrarySearchCall
  | PatientSummaryChunk
  | TextResult
  | ThinkingChunk
  | PdfExportChunk
  | PrintDocumentChunk
  | PatientEvidencePdfChunk
  | EmailPatientEvidenceChunk
  | FormFillChunk
  | MedicalCalculatorChunk
  | VisualDxChunk
  | InteractiveWidgetChunk
  | FormulaCalculatorChunk
  | ClinicalTaskResultChunk
  | CapabilityToolChunk
  | SessionsByDateChunk
  | CsvExportChunk
  | WordExportChunk
  | ExcelExportChunk
  | PptxExportChunk
  | ModeInfoChunk
  | InSessionEvidenceQuotaUpdateChunk
  | QuotaExhaustedChunk
  | MessageInfoChunk
  | AppointmentsChunk
  | SearchTemplatesChunk
  | SearchCommunityTemplatesChunk
  | SearchMyTemplatesChunk
  | GetTemplatePrefsChunk
  | CreateTemplateChunk
  | EditTemplateSectionChunk
  | SearchPatientProfilesChunk
  | UnauthenticatedToolAccessErrorChunk
  | TierRestrictedToolErrorChunk
  | DrugReferencedChunk
  | StreamErrorChunk;

// Reasoning text fragment with its original backend section index
export type ReasoningFragment = readonly [text: string, sectionIndex: number];

// ===== INDEX-BASED TIMELINE ENTRIES =====
export interface UserMessageEntry {
  type: 'user_message';
  id: string;
  content: string;
  timestamp: number;
  position: number;
  files?: Array<{
    name: string;
    /** File extension in uppercase (e.g., "PDF", "PNG"), NOT MIME type */
    type: string;
    file_key?: string; // Unique S3 path for linking citations to attachments
    url?: string; // Presigned URL for displaying images/files
    attachment_id?: string; // UUID for pre-uploaded attachments (null for legacy inline uploads)
  }>;
}

// Each backend index becomes a separate timeline entry
export interface ReasoningItemEntry {
  type: 'reasoning_item';
  id: string;
  content: ReasoningFragment[];
  position: number;
  backendIndex: number; // Needed for component interfaces
  mode?: ChatMode; // Track which mode generated this entry
  thinkingDurationMs?: number; // Persisted duration from backend for reload
}

export interface WebSearchItemEntry {
  type: 'web_search_item';
  id: string;
  query?: string;
  sources: Source[];
  images?: EvidenceImage[];
  status: 'searching' | 'completed';
  position: number;
  backendIndex: number; // Needed for component interfaces
  mode?: ChatMode; // Track which mode generated this entry
  thinkingDurationMs?: number; // Persisted duration from backend for reload
  // EVI-1486: backend message_id (msg_*) — captured at search-entry creation
  // from `streamingState.messageId` (live stream) or `message.message_id`
  // (history reload) so search-block click events join cleanly to backend
  // message rows. The `message_info` chunk that delivers this id always
  // arrives at backendIndex -1, before any web_search_call chunk, so the
  // value is reliably present in both paths.
  messageId?: string;
}

export interface LibrarySearchItemEntry {
  type: 'library_search_item';
  id: string;
  query?: string;
  sources: Source[];
  status: 'searching' | 'completed';
  position: number;
  backendIndex: number; // Needed for component interfaces
  mode?: ChatMode; // Track which mode generated this entry
  thinkingDurationMs?: number; // Persisted duration from backend for reload
  // EVI-1486: backend message_id (msg_*) — see WebSearchItemEntry for rationale.
  messageId?: string;
}

// Patient summary timeline entry (EVI-591: Pre-chart tool events)
export interface PatientSummaryItemEntry {
  type: 'patient_summary_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'fetching' | 'completed' | 'error';
  patientProfileId: string;
  error?: string; // Error message when status is 'error'
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

export interface TextItemEntry {
  type: 'text_item';
  id: string;
  content: string;
  annotations: Annotation[];
  citations?: SimpleCitation[]; // EVIDENCE mode: inline citations per text block
  groupId?: string; // EVIDENCE mode: groups text blocks for inline rendering
  position: number;
  backendIndex: number; // Needed for component interfaces
  mode?: ChatMode; // Track which mode generated this entry
  followUpQuestions?: FollowUpQuestion[]; // Cached follow-up questions for this response
  quotaExceeded?: string; // Set when this is a quota paywall message (value is quota type)
  // EVI-1567 / EV-023: set when the answer references a drug, so a
  // drug-interaction warning badge is rendered above this response.
  drugReferenced?: boolean;
}

export interface ErrorItemEntry {
  type: 'error_item';
  id: string;
  content: string;
  position: number;
}

// EVI-334: Extended thinking timeline entry
export interface ThinkingItemEntry {
  type: 'thinking_item';
  id: string;
  status: 'thinking' | 'streaming' | 'complete';
  thinking?: string; // Accumulated thinking content
  durationMs?: number; // Duration in ms when complete
  position: number;
  backendIndex: number;
  mode?: ChatMode;
  groupId?: string;
}

// PDF export timeline entry
export interface PdfExportItemEntry {
  type: 'pdf_export_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'generating' | 'completed';
  title: string;
  downloadUrl?: string;
  filename?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Print-document timeline entry (web-only)
// Same shape as PdfExportItemEntry plus an error field. On transition
// to 'completed', useChatStream opens the browser print dialog.
export interface PrintDocumentItemEntry {
  type: 'print_document_item';
  id: string;
  toolId: string;
  status: 'generating' | 'completed' | 'error';
  title: string;
  downloadUrl?: string;
  filename?: string;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Patient evidence PDF timeline entry
export interface PatientEvidencePdfItemEntry {
  type: 'patient_evidence_pdf_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'generating' | 'completed' | 'error';
  title: string;
  downloadUrl?: string;
  filename?: string;
  conditionsIncluded?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Email patient evidence timeline entry
export interface EmailPatientEvidenceItemEntry {
  type: 'email_patient_evidence_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'sending' | 'completed' | 'error';
  patientEmail: string;
  message?: string;
  error?: string;
  needsEmail?: boolean;
  needsPdf?: boolean;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// PDF form fill timeline entry
export interface FormFillItemEntry {
  type: 'form_fill_item';
  id: string;
  toolId: string;
  status: 'filling' | 'completed' | 'error' | 'needs_template';
  title: string;
  downloadUrl?: string;
  filename?: string;
  fieldsFilled?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Medical calculator timeline entry (EVI-544)
export interface CalculatorItemEntry {
  type: 'calculator_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  calculator: string; // Calculator ID (e.g., "wells_dvt", "egfr_ckd_epi_2021")
  status: 'calculating' | 'completed' | 'error';
  result?: CalculatorResult; // Full result from backend
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

export interface VisualDxItemEntry {
  type: 'visualdx_item';
  id: string;
  toolId: string;
  tool: 'get_diagnostic_info' | 'enrich_diagnoses_with_links';
  status: 'fetching' | 'completed' | 'error';
  query?: string;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
  /** Structured diagnoses from a completed ``enrich_diagnoses_with_links`` call. */
  diagnoses?: VisualDxDiagnosis[];
  /** Compare-all URL from a completed ``enrich_diagnoses_with_links`` call. */
  compare_url?: string | null;
  /** Structured detail from a completed ``get_diagnostic_info`` call. */
  detail?: VisualDxDetailResult;
}

/**
 * Generic timeline entry for a backend-mounted interactive widget.
 *
 * Mirrors {@link InteractiveWidgetChunk} on the entry side: ``kind``
 * drives which renderer the block mounts, so a single timeline-entry
 * type covers all interactive surfaces.
 */
export interface InteractiveWidgetItemEntry {
  type: 'interactive_widget_item';
  id: string;
  toolId: string;
  kind: InteractiveWidgetKind;
  status: 'fetching' | 'completed' | 'error';
  query?: string;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
  /** Mirrors ``InteractiveWidgetChunk.differential`` on the entry side. */
  differential?: VisualDxRankedDifferentialEntry[];
  gallery?: VisualDxGalleryPayload;
  compare?: VisualDxComparePayload;
  drugReaction?: VisualDxDrugReactionPayload;
  findings?: VisualDxFindingsPayload;
  builtDifferential?: VisualDxBuiltDifferentialPayload;
  handout?: VisualDxHandoutPayload;
}

// Formula calculator timeline entry (EVI-620: Dynamic Formula Calculator)
export interface FormulaCalculatorItemEntry {
  type: 'formula_calculator_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  formulaName: string;
  formulaExpression?: string; // Actual mathematical expression
  formulaDisplay?: string; // Human-readable formula
  category: FormulaCalculatorCategory;
  status: 'calculating' | 'completed' | 'error';
  variables?: Record<string, number | string | boolean>;
  result?: FormulaCalculatorResult;
  sourceUrl?: string;
  sourceName?: string;
  validationReference?: string;
  criteriaBreakdown?: FormulaCriteriaBreakdown[];
  calculationSteps?: FormulaCalculationStep[];
  warnings?: string[];
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Clinical task result timeline entry (EVI-490)
export interface ClinicalTaskResultItemEntry {
  type: 'clinical_task_result_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'creating' | 'completed' | 'failed';
  tasks: ClinicalTaskResultTask[];
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

export interface CapabilityToolItemEntry {
  type: 'capability_tool_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  name: string; // Tool name (e.g., 'get_patient_summary', 'execute_template_workflow')
  status: 'executing' | 'completed' | 'error';
  error?: string; // Error message when status is 'error'
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Sessions by date timeline entry (Patients in a Day feature)
export interface SessionsByDateItemEntry {
  type: 'sessions_by_date_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'fetching' | 'completed' | 'error';
  date: string;
  totalSessions?: number;
  hasMore?: boolean; // True if more sessions exist beyond the returned set
  sessions?: SessionByDateItem[];
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Appointments timeline entry
export interface AppointmentsItemEntry {
  type: 'appointments_item';
  id: string;
  toolId: string;
  status: 'fetching' | 'completed' | 'error';
  date?: string;
  totalAppointments?: number;
  appointments?: AppointmentItem[];
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Search templates timeline entry
export interface SearchTemplatesItemEntry {
  type: 'search_templates_item';
  id: string;
  toolId: string;
  status: 'searching' | 'completed' | 'error';
  query?: string;
  totalResults?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Search community templates timeline entry
export interface SearchCommunityTemplatesItemEntry {
  type: 'search_community_templates_item';
  id: string;
  toolId: string;
  status: 'searching' | 'completed' | 'error';
  query?: string;
  totalResults?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Search user's own templates timeline entry
export interface SearchMyTemplatesItemEntry {
  type: 'search_my_templates_item';
  id: string;
  toolId: string;
  status: 'searching' | 'completed' | 'error';
  query?: string;
  totalResults?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Get template & preferences timeline entry
export interface GetTemplatePrefsItemEntry {
  type: 'get_template_prefs_item';
  id: string;
  toolId: string;
  status: 'fetching' | 'completed' | 'error';
  error?: string;
  templateAvailable?: boolean;
  templateName?: string | null;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Create template timeline entry (Ask Heidi created a personal template)
export interface CreateTemplateItemEntry {
  type: 'create_template_item';
  id: string;
  toolId: string;
  status: 'applying' | 'completed' | 'error';
  templateId?: string;
  templateName?: string;
  reason?: string;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Edit template section timeline entry (Ask Heidi edited a template)
export interface EditTemplateSectionItemEntry {
  type: 'edit_template_section_item';
  id: string;
  toolId: string;
  status: 'applying' | 'completed' | 'error';
  templateId?: string;
  templateName?: string;
  editsApplied?: number;
  reason?: string;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Search patient profiles timeline entry
export interface SearchPatientProfilesItemEntry {
  type: 'search_patient_profiles_item';
  id: string;
  toolId: string;
  status: 'searching' | 'completed' | 'error';
  query?: string;
  totalResults?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// CSV export timeline entry
export interface CsvExportItemEntry {
  type: 'csv_export_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'generating' | 'completed' | 'error';
  filename: string;
  downloadUrl?: string;
  rowCount?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Word document export timeline entry
export interface WordExportItemEntry {
  type: 'word_export_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'generating' | 'completed' | 'error';
  filename: string;
  downloadUrl?: string;
  sessionCount?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// Excel spreadsheet export timeline entry
export interface ExcelExportItemEntry {
  type: 'excel_export_item';
  id: string;
  toolId: string; // Backend tool call ID for tracking updates
  status: 'generating' | 'completed' | 'error';
  filename: string;
  downloadUrl?: string;
  rowCount?: number;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

// PowerPoint presentation export timeline entry
export interface PptxExportItemEntry {
  type: 'pptx_export_item';
  id: string;
  toolId: string;
  status: 'generating' | 'completed' | 'error';
  filename: string;
  downloadUrl?: string;
  previewUrl?: string;
  slideCount?: number;
  presentationId?: string;
  error?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

/**
 * Unsupported tool call timeline entry.
 * Rendered as a graceful fallback when the backend sends a tool call type
 * that this version of the frontend doesn't recognise (e.g. desktop release
 * lagging behind web release).
 */
export interface UnsupportedToolItemEntry {
  type: 'unsupported_tool_item';
  id: string;
  /** The raw tool type string from the backend (e.g. "new_fancy_tool") */
  toolType: string;
  /** Backend tool call ID, if available */
  toolId?: string;
  /** Status string forwarded from the backend chunk, if present */
  status?: string;
  /** Any extra data from the chunk preserved for debugging / future use */
  rawData?: Record<string, unknown>;
  /** FHIR result data for EHR tool calls (EVI-1408 auditability) */
  resultData?: Record<string, unknown>;
  /** Error message for failed tool calls */
  errorMessage?: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

/**
 * Timeline entry for unauthenticated tool access errors.
 * Rendered as a block with the backend message and a sign-up button.
 */
export interface UnauthenticatedToolAccessErrorItemEntry {
  type: 'unauthenticated_tool_access_error_item';
  id: string;
  toolName: string;
  message: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

/**
 * Chunk sent when a tier-restricted user triggers a tool that requires
 * a higher subscription tier (e.g. Clinician plan).
 * Field names match backend (snake_case).
 */
export interface TierRestrictedToolErrorChunk {
  type: 'tier_restricted_tool_error';
  id?: string;
  tool_name?: string;
  status: 'restricted';
  message: string;
  upgrade_url?: string;
  index: number;
}

/**
 * Timeline entry for tier-restricted tool errors.
 * Rendered as a block with the backend message and an upgrade button
 * that opens the Clinician upgrade modal.
 */
export interface TierRestrictedToolErrorItemEntry {
  type: 'tier_restricted_tool_error_item';
  id: string;
  toolName: string;
  message: string;
  position: number;
  backendIndex: number;
  mode?: ChatMode;
}

export interface SlideUpdateChunk {
  type: 'slide_update';
  /**
   * `render_failed` means the slide content was saved but its preview image
   * could not be re-rendered; `image_url` is absent and the on-screen preview
   * is stale until the next successful render.
   */
  status: 'completed' | 'render_failed' | 'error';
  presentation_id: string;
  slide_id: string;
  slide_index?: number;
  image_url?: string;
  pptx_url?: string;
  title?: string;
  error?: string;
  updated_reference_slides?: Array<{
    slide_id: string;
    slide_index: number;
    title: string;
    image_url?: string;
  }>;
}

export type TimelineEntry =
  | UserMessageEntry
  | ReasoningItemEntry
  | WebSearchItemEntry
  | LibrarySearchItemEntry
  | PatientSummaryItemEntry
  | TextItemEntry
  | ErrorItemEntry
  | ThinkingItemEntry
  | PdfExportItemEntry
  | PrintDocumentItemEntry
  | PatientEvidencePdfItemEntry
  | EmailPatientEvidenceItemEntry
  | FormFillItemEntry
  | CalculatorItemEntry
  | VisualDxItemEntry
  | InteractiveWidgetItemEntry
  | FormulaCalculatorItemEntry
  | ClinicalTaskResultItemEntry
  | CapabilityToolItemEntry
  | SessionsByDateItemEntry
  | CsvExportItemEntry
  | WordExportItemEntry
  | ExcelExportItemEntry
  | PptxExportItemEntry
  | AppointmentsItemEntry
  | SearchTemplatesItemEntry
  | SearchCommunityTemplatesItemEntry
  | SearchMyTemplatesItemEntry
  | GetTemplatePrefsItemEntry
  | CreateTemplateItemEntry
  | EditTemplateSectionItemEntry
  | SearchPatientProfilesItemEntry
  | UnsupportedToolItemEntry
  | UnauthenticatedToolAccessErrorItemEntry
  | TierRestrictedToolErrorItemEntry
  | QuotaExhaustedItemEntry;

// ===== STREAMING STATE =====
export interface StreamingState {
  basePosition: number; // Where this response starts in timeline
  activeIndices: Map<number, number>; // backend index → timeline position
  currentActiveIndex: number | null; // Currently streaming index (for UX)
  activityGroupOpen: boolean; // Track if activity group is open for boxing
  streamStartedAt: number; // When the stream began (for timeout tracking)
  lastChunkAt: number; // When the last chunk was received (for inactivity detection)
  lastVisibleContentAt: number; // When visible content (text, search, thinking, tools) last appeared
  streamSlow?: boolean; // Set when no visible content for 30s — UI can show a hint
  abortControllerId?: string; // Optional identifier for tracking abort controller
  messageId?: string; // Backend message_id from message_info chunk, used for feedback tracking
  // EVI-1567 / EV-023: set once a <drug-referenced/> signal arrives so any text
  // blocks in the current response (already-seen or still-to-arrive) are marked
  // for the drug-interaction warning badge.
  drugReferenced?: boolean;
}

export interface ConversationMetadata {
  lastActivity: number;
  pendingMode?: ChatMode; // Mode for the pending message
  clientChatId?: string; // Client-generated ObjectId to prevent race conditions
  /**
   * EVI-1403: timestamp written by EvidenceSBSOverlay whenever it dismisses
   * (confirm success, skip success, confirm error fallback, or 60s
   * hung-stream backstop). Read by useChat's page-load recovery effect to
   * suppress `useStreamRecovery` for ~10s while the backend persists the
   * confirmed candidate (or while the SBS session quietly finishes after a
   * backstop). Without this the user sees a misleading "Reconnecting…"
   * indicator over a chat that's about to populate naturally via refetch.
   */
  recentlySBSConfirmedAt?: number;
  /**
   * EVI-1680: set on the optimistic `session-${sessionId}-new` state when a new
   * in-session chat is eager-created so Evidence SBS setup can fire on the first
   * message. Signals `useSessionlessChatMigration` to resume the (deferred)
   * stream once the chat is selected in the sidebar and remounts under
   * `chat-${clientChatId}`. Normal in-session chats never set this, so their
   * lazy backend-creation flow is unaffected.
   */
  sbsPendingResume?: boolean;
}

// Main session conversation structure - COMPLETELY NEW
export interface SessionConversation {
  timeline: TimelineEntry[]; // Chronological order of all entries
  streamingState: StreamingState | null; // Active streaming management
  metadata: ConversationMetadata; // Conversation stats
}

// ===== API INTERFACE TYPES =====
// For message history
export interface Message {
  role: 'user' | 'assistant';
  content: string;
}
